import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import Button from '../../../../components/Button';
import Checkbox from '../../../../components/Checkbox';
import SelectComponent from '../../../../components/SelectComponent/SelectComponent';
import {
  BookingModuleSelectors,
  setOwnerBookingModuleThemeColorValidity,
  updateOwnerBookingModuleDraft,
} from '../../../../redux/reducers/bookingModuleSlice';
import { setMessage } from '../../../../redux/reducers/pageSlice';
import {
  OwnerBookingModule,
  OwnerBookingModuleUpdate,
} from '../../../../redux/types/bookingModuleTypes';
import {
  ButtonType,
  MessageTypes,
  OptionsListType,
} from '../../../../utils/@globalTypes';
import styles from './BookingModuleCard.module.scss';

const backgroundFillOptions: OptionsListType = [
  { value: 'white', label: 'Белый' },
  { value: 'blurred', label: 'Прозрачный заблюренный' },
];

type BookingModuleCardProps = {
  module: OwnerBookingModule;
  defaultExpanded?: boolean;
};

const BookingModuleCard = ({
  module,
  defaultExpanded = false,
}: BookingModuleCardProps) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const isSaving = useSelector(BookingModuleSelectors.getIsSaving);
  const isLoading = useSelector(BookingModuleSelectors.getIsLoading);
  const isModuleSaving = useSelector(
    BookingModuleSelectors.getSavingById(module.id),
  );
  const draft = useSelector(BookingModuleSelectors.getDraftById(module.id));
  const saveError = useSelector(BookingModuleSelectors.getSaveError(module.id));
  const isThemeColorInvalid = useSelector(
    BookingModuleSelectors.getThemeColorInvalid(module.id),
  );
  const slugError = useSelector(BookingModuleSelectors.getSlugError(module.id));
  const settings = { ...module, ...draft };
  const isEditingDisabled = isSaving || isLoading;
  const [themeColorInput, setThemeColorInput] = useState(settings.theme_color);

  useEffect(() => {
    setThemeColorInput(settings.theme_color);
  }, [settings.theme_color]);

  const updateSettings = (data: OwnerBookingModuleUpdate) => {
    dispatch(updateOwnerBookingModuleDraft({ id: module.id, data }));
  };

  const copyValue = async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      dispatch(
        setMessage({
          status: MessageTypes.POSITIVE,
          code: 200,
          message: `${label} скопирована в буфер обмена`,
        }),
      );
    } catch {
      dispatch(
        setMessage({
          status: MessageTypes.ERROR,
          code: 500,
          message: 'Не удалось скопировать. Проверьте разрешение браузера.',
        }),
      );
    }
  };

  const setThemeColor = (value: string) => {
    setThemeColorInput(value);
    const isValid = /^#[0-9a-fA-F]{6}$/.test(value);
    dispatch(
      setOwnerBookingModuleThemeColorValidity({
        id: module.id,
        isValid,
      }),
    );
    if (isValid) updateSettings({ theme_color: value.toUpperCase() });
  };

  const setThemeColorFromPicker = (value: string) => {
    setThemeColorInput(value.toUpperCase());
    dispatch(
      setOwnerBookingModuleThemeColorValidity({
        id: module.id,
        isValid: true,
      }),
    );
    updateSettings({ theme_color: value.toUpperCase() });
  };

  return (
    <article
      className={`${styles.moduleCard} ${
        isExpanded ? styles.moduleCardExpanded : ''
      }`}
      aria-labelledby={`module-title-${module.id}`}
    >
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>
          <button
            className={styles.accordionTrigger}
            type="button"
            aria-expanded={isExpanded}
            aria-controls={`module-settings-${module.id}`}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            <span id={`module-title-${module.id}`}>{module.name}</span>
            <span className={styles.headerTrailing}>
              {isModuleSaving && (
                <span className={styles.saving}>Сохраняем…</span>
              )}
              <span
                className={`${styles.chevron} ${
                  isExpanded ? styles.chevronExpanded : ''
                }`}
                aria-hidden="true"
              />
            </span>
          </button>
        </h3>
      </div>

      <div
        id={`module-settings-${module.id}`}
        className={`${styles.accordionPanel} ${
          isExpanded ? styles.accordionPanelExpanded : ''
        }`}
        aria-hidden={!isExpanded}
      >
        <div className={styles.accordionContent}>
          <div className={styles.moduleSettings}>
            <Checkbox
              checked={settings.booking_page_enabled}
              name={`booking-page-${module.id}`}
              label="Страница Букинга включена"
              disabled={isEditingDisabled}
              onChange={(value) =>
                updateSettings({ booking_page_enabled: value })
              }
            />
            <Checkbox
              checked={settings.integration_enabled}
              name={`booking-integration-${module.id}`}
              label="Модульная Интеграция включена"
              disabled={isEditingDisabled}
              onChange={(value) =>
                updateSettings({ integration_enabled: value })
              }
            />
          </div>

          <div className={styles.preset}>
            <SelectComponent
              id={`background-fill-${module.id}`}
              name={`background-fill-${module.id}`}
              title="Выбор заливки фона"
              currentValue={settings.background_fill}
              optionsList={backgroundFillOptions}
              setSelecValue={(value) =>
                updateSettings({
                  background_fill: value as 'white' | 'blurred',
                })
              }
              isDisabled={isEditingDisabled}
              isSearchable={false}
            />
            <p className={styles.hint}>
              Применяется к Странице Букинга и Модульной Интеграции.
            </p>
          </div>

          <div className={styles.themeColor}>
            <label className={styles.themeColorLabel}>
              Тема Модульной Интеграции: кнопка и надпись «Бронирование»
            </label>
            <div className={styles.colorControls}>
              <input
                aria-label="Цвет темы"
                className={styles.colorPicker}
                type="color"
                value={settings.theme_color}
                disabled={isEditingDisabled}
                onChange={(event) =>
                  setThemeColorFromPicker(event.target.value)
                }
              />
              <label className={styles.hexField}>
                <span>HEX</span>
                <input
                  aria-label="HEX цвета темы"
                  type="text"
                  value={themeColorInput}
                  maxLength={7}
                  placeholder="#1855B7"
                  disabled={isEditingDisabled}
                  aria-invalid={isThemeColorInvalid}
                  onChange={(event) => setThemeColor(event.target.value)}
                />
              </label>
            </div>
            <p className={styles.hint}>
              Применяется только к Модульной Интеграции.
            </p>
            {isThemeColorInvalid && (
              <p className={styles.inputError} role="alert">
                Укажите цвет в формате #RRGGBB.
              </p>
            )}
          </div>

          <div className={styles.slugField}>
            <label htmlFor={`booking-module-slug-${module.id}`}>
              Адрес модуля (slug)
            </label>
            <input
              id={`booking-module-slug-${module.id}`}
              type="text"
              value={settings.slug}
              disabled={isEditingDisabled}
              aria-invalid={Boolean(slugError)}
              aria-describedby={
                slugError
                  ? `booking-module-slug-error-${module.id}`
                  : `booking-module-slug-hint-${module.id}`
              }
              onChange={(event) => updateSettings({ slug: event.target.value })}
            />
            {slugError ? (
              <p
                className={styles.inputError}
                id={`booking-module-slug-error-${module.id}`}
                role="alert"
              >
                {slugError}
              </p>
            ) : (
              <p
                className={styles.hint}
                id={`booking-module-slug-hint-${module.id}`}
              >
                Это значение используется в адресе Страницы Букинга.
              </p>
            )}
          </div>

          <div className={styles.shareSection}>
            <h4>Страница Букинга</h4>
            <div className={styles.copyRow}>
              <a href={module.public_url} target="_blank" rel="noreferrer">
                {module.public_url}
              </a>
              <Button
                title="Копировать ссылку"
                type={ButtonType.SECONDARY_SMALL}
                onClick={() => copyValue(module.public_url, 'Ссылка')}
              />
            </div>
          </div>

          <div className={styles.shareSection}>
            <h4>Код для сайта</h4>
            <pre className={styles.embedCode}>
              <code>{module.embed_html}</code>
            </pre>
            <Button
              title="Копировать HTML-код"
              type={ButtonType.SECONDARY_SMALL}
              onClick={() => copyValue(module.embed_html, 'HTML-код')}
            />
          </div>

          {saveError && (
            <div className={styles.saveError} role="alert">
              {saveError === 'free_tariff' ? (
                <>
                  <p>Для работы с Модулем бронирования нужен платный тариф.</p>
                  <Link to="/prices">Посмотреть тарифы</Link>
                </>
              ) : saveError === 'not_found' ? (
                <p>Модуль не найден. Обновите страницу и попробуйте снова.</p>
              ) : (
                <p>Не удалось сохранить настройки модуля</p>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

export default BookingModuleCard;
